import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { Add, Remove, CloudUpload, Search } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { parseCoordinates } from "../api/FlaskAPI";
import { getErrorMessage } from "../api/errors";
import { CRS_OPTIONS } from "../utils/crs_options";
import { useTranslation } from "react-i18next";

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function detectLatLngColumns(headers) {
  let latCol = null;
  let lngCol = null;
  headers.forEach((header, idx) => {
    const lower = String(header).toLowerCase().trim();
    if (
      latCol === null &&
      (lower.includes("lat") || lower.includes("latitude"))
    ) {
      latCol = idx;
    }
    if (
      lngCol === null &&
      (lower.includes("lng") ||
        lower.includes("lon") ||
        lower.includes("longitude"))
    ) {
      lngCol = idx;
    }
  });
  return { latCol, lngCol };
}

export default function CoordinatesDialog({
  componentSource,
  open,
  onClose,
  onCoordinatesApplied,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Manual input state
  const [rows, setRows] = useState([
    { lat: "", lng: "" },
    { lat: "", lng: "" },
    { lat: "", lng: "" },
  ]);

  // File upload state
  const [fileName, setFileName] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [latColumn, setLatColumn] = useState("");
  const [lngColumn, setLngColumn] = useState("");
  const [selectedCrs, setSelectedCrs] = useState("EPSG:4326");

  const handleTabChange = (_, newValue) => {
    setTab(newValue);
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, { lat: "", lng: "" }]);
  };

  const handleRemoveRow = (index) => {
    if (rows.length <= 3) return;
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRowChange = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  const handleApply = async () => {
    let coordinates = [];

    if (tab === 0) {
      // Manual mode
      coordinates = rows
        .map((r) => {
          const lat = parseFloat(r.lat);
          const lng = parseFloat(r.lng);
          return [lat, lng];
        })
        .filter(([lat, lng]) => !isNaN(lat) && !isNaN(lng));

      if (coordinates.length < 3) {
        toast.error(t("coords.toasts.minManual"));
        return;
      }
    } else {
      // File mode
      if (!latColumn || !lngColumn) {
        toast.error(t("coords.toasts.selectColumns"));
        return;
      }

      const latIdx = parseInt(latColumn, 10);
      const lngIdx = parseInt(lngColumn, 10);

      coordinates = allData
        .map((row) => {
          const lat = parseFloat(row[latIdx]);
          const lng = parseFloat(row[lngIdx]);
          return [lat, lng];
        })
        .filter(([lat, lng]) => !isNaN(lat) && !isNaN(lng));

      if (coordinates.length < 3) {
        toast.error(t("coords.toasts.minFile"));
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await parseCoordinates({
        check_in_project: componentSource !== "project",
        coordinates,
        crs: selectedCrs,
      });
      if (response.status !== 200) {
        toast.error(response?.data?.message || t("coords.toasts.parseFailed"));
        return;
      }

      const { coordinates: response_coordinates, is_area_valid } =
        response.data;

      if (!response_coordinates) {
        toast.error(t("coords.toasts.badResponse"));
        return;
      }

      onCoordinatesApplied({
        polyId: `uploaded-${Date.now()}`,
        coordinates: response_coordinates,
        is_area_valid,
      });

      if (!is_area_valid) {
        toast.warning(t("coords.toasts.invalidArea"));
      } else {
        toast.success(t("coords.toasts.applied"));
      }
      resetState();
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error, t("coords.toasts.parseFailed")));
    } finally {
      setSubmitting(false);
    }
  };

  const resetState = () => {
    setRows([
      { lat: "", lng: "" },
      { lat: "", lng: "" },
      { lat: "", lng: "" },
    ]);
    setFileName(null);
    setHeaders([]);
    setPreviewData([]);
    setAllData([]);
    setLatColumn("");
    setLngColumn("");
    setTab(0);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setFileName(file.name);

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            blankrows: false,
          });

          if (jsonData.length < 4) {
            toast.error(t("coords.toasts.minRows"));
            return;
          }

          const fileHeaders = jsonData[0].map(String);
          const fileRows = jsonData.slice(1);

          const { latCol, lngCol } = detectLatLngColumns(fileHeaders);

          setHeaders(fileHeaders);
          setPreviewData(fileRows.slice(0, 5));
          setAllData(fileRows);
          setLatColumn(latCol !== null ? String(latCol) : "");
          setLngColumn(lngCol !== null ? String(lngCol) : "");
        } catch (err) {
          toast.error(t("coords.toasts.fileFailed", { error: err.message }));
        }
      };
      reader.readAsArrayBuffer(file);
    },
    [t],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    multiple: false,
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("coords.title")}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, mb: 2 }}>
          <Autocomplete
            freeSolo
            options={CRS_OPTIONS}
            value={selectedCrs}
            onChange={(_, newValue) => setSelectedCrs(newValue || "")}
            onInputChange={(_, newInputValue) => setSelectedCrs(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("coords.crs")}
                size="small"
                placeholder={t("coords.crsPlaceholder")}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      <Search sx={{ color: "action.active", mr: 1 }} />
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab label={t("coords.manual")} />
          <Tab label={t("coords.file")} />
        </Tabs>

        {/* Manual Input Tab */}
        <TabPanel value={tab} index={0}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("coords.latitude")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("coords.longitude")}
                  </TableCell>
                  <TableCell sx={{ width: "50px" }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.lat}
                        onChange={(e) =>
                          handleRowChange(index, "lat", e.target.value)
                        }
                        placeholder={t("coords.example", { value: "30.0444" })}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.lng}
                        onChange={(e) =>
                          handleRowChange(index, "lng", e.target.value)
                        }
                        placeholder={t("coords.example", { value: "31.2357" })}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveRow(index)}
                        aria-label={t("coords.removeRow")}
                        disabled={rows.length <= 3}
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button
              size="small"
              startIcon={<Add />}
              onClick={handleAddRow}
              sx={{ textTransform: "none", alignSelf: "flex-start" }}
            >
              {t("coords.addRow")}
            </Button>
          </Box>
        </TabPanel>

        {/* File Upload Tab */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {!fileName ? (
              <Box
                {...getRootProps()}
                sx={{
                  border: "2px dashed",
                  borderColor: isDragActive ? "primary.main" : "grey.400",
                  borderRadius: "10px",
                  p: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "border-color 0.2s",
                  bgcolor: isDragActive ? "action.hover" : "transparent",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "action.hover",
                  },
                }}
              >
                <input {...getInputProps()} />
                <CloudUpload
                  sx={{ color: "primary.main", fontSize: 32, mb: 1 }}
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {isDragActive ? t("upload.drop") : t("coords.uploadPrompt")}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {t("coords.supported")}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "success.main",
                  borderRadius: "10px",
                  p: 2,
                  bgcolor: "success.light",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "success.dark" }}
                >
                  {t("coords.fileLoaded", { name: fileName })}
                </Typography>
              </Box>
            )}

            {headers.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t("coords.latColumn")}</InputLabel>
                    <Select
                      value={latColumn}
                      label={t("coords.latColumn")}
                      onChange={(e) => setLatColumn(e.target.value)}
                    >
                      {headers.map((header, idx) => (
                        <MenuItem key={idx} value={String(idx)}>
                          {header}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t("coords.lngColumn")}</InputLabel>
                    <Select
                      value={lngColumn}
                      label={t("coords.lngColumn")}
                      onChange={(e) => setLngColumn(e.target.value)}
                    >
                      {headers.map((header, idx) => (
                        <MenuItem key={idx} value={String(idx)}>
                          {header}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {t("coords.preview", { count: previewData.length })}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {headers.map((h, i) => (
                        <TableCell key={i} sx={{ fontWeight: 600 }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.map((row, i) => (
                      <TableRow key={i}>
                        {row.map((cell, j) => (
                          <TableCell key={j}>{String(cell)}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px" }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={submitting}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("coords.apply")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
